import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Play, Square, ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import { atlasApi, AtlasUser, FeedbackCiclo, FeedbackPerguntaTipo } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  onVerRelatorio: (cicloId: string, funccodi: number) => void;
}

const STATUS: Record<string, { label: string; classes: string }> = {
  rascunho: { label: 'Rascunho', classes: 'bg-gray-100 text-gray-600' },
  ativo: { label: 'Ativo', classes: 'bg-emerald-100 text-emerald-700' },
  encerrado: { label: 'Encerrado', classes: 'bg-rose-100 text-rose-700' },
};

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200';

interface PerguntaForm { texto: string; tipo: FeedbackPerguntaTipo; categoria: string; }

const GerenciarCiclos: React.FC<Props> = ({ user, onVerRelatorio }) => {
  const [ciclos, setCiclos] = useState<FeedbackCiclo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [form, setForm] = useState({ titulo: '', descricao: '', data_inicio: '', data_fim: '', participantes: '' });
  const [perguntas, setPerguntas] = useState<PerguntaForm[]>([{ texto: '', tipo: 'escala_1_5', categoria: 'comportamental' }]);

  const carregar = useCallback(() => {
    setLoading(true);
    atlasApi.listarCiclos(user)
      .then(r => setCiclos(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const acao = async (fn: Promise<any>) => {
    try { await fn; carregar(); } catch (e: any) { setErro(e.message || 'Erro'); }
  };

  const salvarNovo = async () => {
    if (!form.titulo.trim()) { setErro('Título obrigatório.'); return; }
    setSalvando(true);
    setErro(null);
    const participantes = form.participantes.split(/[\s,]+/).map(s => parseInt(s, 10)).filter(n => !isNaN(n));
    const body = {
      titulo: form.titulo, descricao: form.descricao, data_inicio: form.data_inicio || undefined, data_fim: form.data_fim || undefined,
      perguntas: perguntas.filter(p => p.texto.trim()).map((p, i) => ({ id: i + 1, ...p })),
      participantes_funccodi: participantes,
    };
    try {
      await atlasApi.criarCiclo(user, body);
      setNovoAberto(false);
      setForm({ titulo: '', descricao: '', data_inicio: '', data_fim: '', participantes: '' });
      setPerguntas([{ texto: '', tipo: 'escala_1_5', categoria: 'comportamental' }]);
      carregar();
    } catch (e: any) { setErro(e.message || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex-1">Gerenciar ciclos de feedback</h2>
        <button onClick={() => setNovoAberto(o => !o)} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> Novo ciclo
        </button>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {novoAberto && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
          <input className={inputCls} placeholder="Título do ciclo" value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} />
          <textarea className={`${inputCls} min-h-[60px]`} placeholder="Descrição" value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} type="date" value={form.data_inicio} onChange={e => setForm(p => ({ ...p, data_inicio: e.target.value }))} />
            <input className={inputCls} type="date" value={form.data_fim} onChange={e => setForm(p => ({ ...p, data_fim: e.target.value }))} />
          </div>
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Participantes (funccodi separados por vírgula/espaço)</div>
            <textarea className={`${inputCls} min-h-[50px]`} placeholder="12534, 12876, ..." value={form.participantes} onChange={e => setForm(p => ({ ...p, participantes: e.target.value }))} />
          </div>
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Perguntas</div>
            {perguntas.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 mb-2">
                <input className={inputCls} placeholder="Texto da pergunta" value={p.texto} onChange={e => setPerguntas(arr => arr.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))} />
                <select className={inputCls} value={p.tipo} onChange={e => setPerguntas(arr => arr.map((x, j) => j === i ? { ...x, tipo: e.target.value as FeedbackPerguntaTipo } : x))}>
                  <option value="escala_1_5">Escala 1-5</option>
                  <option value="sim_nao">Sim/Não</option>
                  <option value="texto_livre">Texto livre</option>
                </select>
                <select className={inputCls} value={p.categoria} onChange={e => setPerguntas(arr => arr.map((x, j) => j === i ? { ...x, categoria: e.target.value } : x))}>
                  <option value="comportamental">Comportamental</option>
                  <option value="tecnico">Técnico</option>
                  <option value="lideranca">Liderança</option>
                </select>
              </div>
            ))}
            <button onClick={() => setPerguntas(a => [...a, { texto: '', tipo: 'escala_1_5', categoria: 'comportamental' }])} className="text-sm text-purple-600 hover:underline">+ adicionar pergunta</button>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setNovoAberto(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={salvarNovo} disabled={salvando} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Criar ciclo
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : ciclos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">Nenhum ciclo criado.</div>
      ) : (
        <ul className="space-y-2">
          {ciclos.map(c => {
            const st = STATUS[c.status] || { label: c.status, classes: 'bg-gray-100 text-gray-500' };
            const aberto = expandido === c._id;
            return (
              <li key={c._id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="p-4 flex items-center gap-3">
                  <button onClick={() => setExpandido(aberto ? null : c._id)} className="text-gray-400">
                    {aberto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{c.titulo}</div>
                    <div className="text-xs text-gray-400">{(c.participantes_funccodi || []).length} participantes · {(c.perguntas || []).length} perguntas</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.classes}`}>{st.label}</span>
                  {c.status === 'rascunho' && (
                    <button onClick={() => acao(atlasApi.publicarCiclo(user, c._id))} className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium flex items-center gap-1 hover:bg-emerald-100"><Play className="w-3.5 h-3.5" /> Publicar</button>
                  )}
                  {c.status === 'ativo' && (
                    <button onClick={() => acao(atlasApi.encerrarCiclo(user, c._id))} className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-medium flex items-center gap-1 hover:bg-rose-100"><Square className="w-3.5 h-3.5" /> Encerrar</button>
                  )}
                </div>
                {aberto && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2">Participantes</div>
                    {(c.participantes_funccodi || []).length === 0 ? (
                      <p className="text-sm text-gray-400">Sem participantes.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(c.participantes_funccodi || []).map(fc => (
                          <button key={fc} onClick={() => onVerRelatorio(c._id, fc)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:border-purple-300 flex items-center gap-1">
                            <BarChart3 className="w-3.5 h-3.5 text-purple-500" /> #{fc}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default GerenciarCiclos;
